'use client';

import { JSX, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { AtIcon } from '../at-icon';
import classNames from 'classnames';
import { sendGTMFormErrorEvent } from '@/components/helpers/analytics/gtm';
import {
  IAtDropdownFieldProps,
  ISelectOption,
} from './at-dropdown-field.types';

/**
 * `AtDropdownField` is a styled, accessible dropdown (`<select>`) component
 * built exclusively for use within React Hook Form.
 *
 * It supports required validation, live error messaging, GTM form error event tracking,
 * and optional icon support for enhanced UI.
 *
 * This component must be used inside a `FormProvider` context.
 * It is not intended for UI filtering or uncontrolled input — use `AtDropdown` for that.
 */

export const AtDropdownField = ({
  id,
  label,
  icon,
  className,
  required,
  options,
  ...props
}: IAtDropdownFieldProps): JSX.Element => {
  const {
    register,
    formState: { errors, isSubmitting },
    getValues,
  } = useFormContext();
  const hasError = Boolean(errors[id]);
  const errorMessage = hasError ? (errors[id]?.message as string) : '';

  useEffect(() => {
    if (hasError) {
      sendGTMFormErrorEvent({
        form_name: getValues('formName'),
        form_field: id,
        error_message: errorMessage,
      });
    }
  }, [errorMessage, getValues, hasError, id]);

  return (
    <div className="w-full">
      <div
        className={classNames(
          'relative flex h-10 items-center rounded-15 border-2 md:h-[3.938rem]',
          {
            'border-skin-error focus:outline-red-400': hasError,
            'border-skin-stroke-secondary': !hasError,
          },
        )}
      >
        {icon && (
          <span className="absolute left-3 text-24">
            <AtIcon icon={icon} color="var(--color-ui-icon-tertiary)" />
          </span>
        )}
        {label && (
          <label
            htmlFor={id}
            className={classNames(
              `absolute -top-3 left-4 h-5 bg-skin-primary px-1 text-10 text-page-link md:-top-[0.75rem] md:text-sm`,
              {
                'after:ml-0.5 after:text-skin-alert after:content-["*"]':
                  required,
                'text-skin-error': hasError,
              },
            )}
          >
            {label}
          </label>
        )}
        <select
          {...register(id, {
            required,
            disabled: props.disabled || isSubmitting,
          })}
          aria-label={label}
          id={id}
          aria-required={required}
          {...(hasError
            ? {
                'aria-invalid': hasError,
                'aria-describedby': `errorMessage-${id}`,
              }
            : {})}
          className={classNames(
            `custom-select w-full appearance-none rounded-15 bg-transparent py-2 pr-3 text-sm focus:outline-none md:py-4 md:text-lg`,
            className,
            {
              'pl-10 md:pl-11': icon,
              'pl-4': !icon,
            },
          )}
        >
          {options.map(({ label, value }: ISelectOption, index) => (
            <option key={`${value}-${index}`} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {hasError && (
        <p
          id={`errorMessage-${id}`}
          className="mt-2 pl-4 text-xs text-skin-alert"
          role="alert"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
